const DARK_CLASS = 'dark';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

export const COLOR_SCHEME_STORAGE_KEY = 'tc_color_scheme';
export type ColorSchemePreference = 'system' | 'light' | 'dark';

/** Apply or remove the Spartan `.dark` class on `<html>`. */
export function applySystemColorScheme(isDark: boolean): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.classList.toggle(DARK_CLASS, isDark);
}

/** Read OS / browser dark mode preference. */
export function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia(MEDIA_QUERY).matches;
}

export function readStoredColorSchemePreference(): ColorSchemePreference {
  if (typeof localStorage === 'undefined') {
    return 'system';
  }
  try {
    const stored = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    /* private mode */
  }
  return 'system';
}

export function persistColorSchemePreference(
  preference: ColorSchemePreference,
): void {
  try {
    localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
  } catch {
    /* private mode */
  }
}

export function resolveColorSchemeIsDark(
  preference: ColorSchemePreference,
  systemPrefersDark = readSystemPrefersDark(),
): boolean {
  if (preference === 'dark') {
    return true;
  }
  if (preference === 'light') {
    return false;
  }
  return systemPrefersDark;
}

/** Apply theme before Angular boot — use in index.html inline script. */
export function applyInitialColorSchemeFromStorage(): void {
  const preference = readStoredColorSchemePreference();
  applySystemColorScheme(resolveColorSchemeIsDark(preference));
}

/**
 * @deprecated Use {@link ColorSchemeService} via `provideSystemColorScheme()`.
 */
let activeCleanup: (() => void) | null = null;

/** @deprecated Use {@link ColorSchemeService} via `provideSystemColorScheme()`. */
export function initSystemColorScheme(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  activeCleanup?.();

  const media = window.matchMedia(MEDIA_QUERY);
  const sync = (): void => applySystemColorScheme(media.matches);
  sync();
  media.addEventListener('change', sync);

  const cleanup = (): void => {
    media.removeEventListener('change', sync);
    if (activeCleanup === cleanup) {
      activeCleanup = null;
    }
  };
  activeCleanup = cleanup;
  return cleanup;
}
